import { useEffect, useState } from 'react';
import {
  MetricSubsection,
  ThresholdPreviewChips,
  ThresholdValueUnitRow,
  avgDurationThresholdRowTemplateColumns,
  buildPreviewValues,
  frequencyThresholdRowTemplateColumns,
  isSingularValue,
  toFrequencyPreviewUnit,
} from '@/features/projects/components/metricThresholds';
import {
  avgDurationUnitOptions,
  frequencyUnitOptions,
  type MetricThresholdDrafts,
  parseAvgDurationSeconds,
  parseFrequencyPerDay,
  parseThresholdValue,
  type PreferredUnits,
} from '@/features/projects/metricThresholdDrafts';
import { s, type SpacingToken } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Box, Divider, Inline, Panel, Stack, Text } from '@/ui';
import { settingsCopyTextSx } from '@/ui/composites/settingsPageTypographySx';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatFrequency } from '@/ui/formatters/formatFrequency';
import { Slider, Switch } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import type { Dispatch, ReactElement, RefObject, SetStateAction } from 'react';

type MetricThresholdsSectionProps = {
  title: string;
  description: string;
  passRateLabel?: string;
  frequencyLabel?: string;
  drafts: MetricThresholdDrafts;
  setDrafts: Dispatch<SetStateAction<MetricThresholdDrafts>>;
  preferredUnitsRef: RefObject<PreferredUnits>;
  initialEnabled: MetricThresholdEnabledState;
  isDirty: boolean;
  isValid: boolean;
  isPending: boolean;
  baselineKey?: string;
  gridSpacing: SpacingToken;
  container?: 'panel' | 'none';
  onCancel: () => void;
  onSubmit: (enabled: MetricThresholdEnabledState) => void;
};

export type MetricThresholdEnabledState = {
  passRate: boolean;
  frequency: boolean;
  avgDuration: boolean;
};

export const MetricThresholdsSection = ({
  title,
  description,
  passRateLabel = 'Pass Rate',
  frequencyLabel = 'Run Frequency',
  drafts,
  setDrafts,
  preferredUnitsRef,
  initialEnabled,
  isDirty,
  isValid: _isValid,
  isPending,
  baselineKey,
  gridSpacing: _gridSpacing,
  container = 'panel',
  onCancel,
  onSubmit,
}: MetricThresholdsSectionProps): ReactElement => {
  const partialInitialEnabled = initialEnabled as Partial<MetricThresholdEnabledState> | undefined;
  const resolvedInitialEnabled: MetricThresholdEnabledState = {
    passRate: partialInitialEnabled?.passRate ?? true,
    frequency: partialInitialEnabled?.frequency ?? true,
    avgDuration: partialInitialEnabled?.avgDuration ?? true,
  };
  const [enabled, setEnabled] = useState<MetricThresholdEnabledState>(resolvedInitialEnabled);
  const [hasInteracted, setHasInteracted] = useState(false);
  const partialDrafts = drafts as
    | Partial<Pick<MetricThresholdDrafts, 'passRate' | 'frequency' | 'avgDuration'>>
    | undefined;
  const passRateDraft = partialDrafts?.passRate ?? { target: '', warn: '' };
  const frequencyDraft = partialDrafts?.frequency ?? {
    targetValue: '',
    targetUnit: 'days',
    warnValue: '',
    warnUnit: 'days',
  };
  const avgDurationDraft = partialDrafts?.avgDuration ?? {
    targetValue: '',
    targetUnit: 'seconds',
    warnValue: '',
    warnUnit: 'seconds',
  };

  useEffect(() => {
    setEnabled(resolvedInitialEnabled);
  }, [
    resolvedInitialEnabled.passRate,
    resolvedInitialEnabled.frequency,
    resolvedInitialEnabled.avgDuration,
  ]);

  useEffect(() => {
    setHasInteracted(false);
  }, [baselineKey]);

  const isEnabledDirty =
    enabled.passRate !== resolvedInitialEnabled.passRate ||
    enabled.frequency !== resolvedInitialEnabled.frequency ||
    enabled.avgDuration !== resolvedInitialEnabled.avgDuration;
  const hasPendingChanges = (isDirty || isEnabledDirty) && hasInteracted;

  const passRateValid =
    parseThresholdValue(passRateDraft.warn) !== null &&
    parseThresholdValue(passRateDraft.target) !== null;
  const frequencyValid =
    parseFrequencyPerDay(frequencyDraft.warnValue, frequencyDraft.warnUnit) !== null &&
    parseFrequencyPerDay(frequencyDraft.targetValue, frequencyDraft.targetUnit) !== null;
  const avgDurationValid =
    parseAvgDurationSeconds(avgDurationDraft.warnValue, avgDurationDraft.warnUnit) !== null &&
    parseAvgDurationSeconds(avgDurationDraft.targetValue, avgDurationDraft.targetUnit) !== null;

  const canSubmit =
    hasPendingChanges &&
    !isPending &&
    (!enabled.passRate || passRateValid) &&
    (!enabled.frequency || frequencyValid) &&
    (!enabled.avgDuration || avgDurationValid);

  const executionSubjectLabel = (value: string): string => {
    const singular = isSingularValue(value);
    if (title.startsWith('Suite ')) {
      return singular ? 'suite run per' : 'suite runs per';
    }
    if (title.startsWith('Execution Run ')) {
      return singular ? 'execution run per' : 'execution runs per';
    }
    if (title.startsWith('Test ')) {
      return singular ? 'test run per' : 'test runs per';
    }
    return singular ? 'pipeline job per' : 'pipeline jobs per';
  };

  const avgDurationPerLabel = title.startsWith('Suite ')
    ? 'per suite run'
    : title.startsWith('Execution Run ')
      ? 'per execution run'
      : title.startsWith('Test ')
        ? 'per test run'
        : 'per pipeline job';

  const parsedPassRateTarget = parseThresholdValue(passRateDraft.target);
  const parsedPassRateWarn = parseThresholdValue(passRateDraft.warn);
  const passRateTargetValue = Math.min(100, Math.max(0, parsedPassRateTarget ?? 90));
  const passRateWarnValue = Math.min(100, Math.max(0, parsedPassRateWarn ?? 75));
  const passRateRange: [number, number] =
    passRateWarnValue <= passRateTargetValue
      ? [passRateWarnValue, passRateTargetValue]
      : [passRateTargetValue, passRateWarnValue];

  const passRateGradient = `linear-gradient(90deg, ${uiTokens.statusColors.red} 0%, ${uiTokens.statusColors.red} ${passRateRange[0]}%, ${uiTokens.statusColors.orange} ${passRateRange[0]}%, ${uiTokens.statusColors.orange} ${passRateRange[1]}%, ${uiTokens.statusColors.green} ${passRateRange[1]}%, ${uiTokens.statusColors.green} 100%)`;
  const passRateRailBackground = enabled.passRate
    ? passRateGradient
    : uiTokens.statusColors.neutral;

  const frequencyWarnPerDay = parseFrequencyPerDay(
    frequencyDraft.warnValue,
    frequencyDraft.warnUnit,
  );
  const frequencyTargetPerDay = parseFrequencyPerDay(
    frequencyDraft.targetValue,
    frequencyDraft.targetUnit,
  );
  const avgDurationWarnSeconds = parseAvgDurationSeconds(
    avgDurationDraft.warnValue,
    avgDurationDraft.warnUnit,
  );
  const avgDurationTargetSeconds = parseAvgDurationSeconds(
    avgDurationDraft.targetValue,
    avgDurationDraft.targetUnit,
  );

  const frequencyThresholds =
    frequencyWarnPerDay !== null && frequencyTargetPerDay !== null
      ? {
          warn: frequencyWarnPerDay,
          target: frequencyTargetPerDay,
          direction: 'higherBetter' as const,
        }
      : null;

  const previewFrequencyUnit =
    frequencyDraft.warnUnit === frequencyDraft.targetUnit
      ? toFrequencyPreviewUnit(frequencyDraft.warnUnit)
      : undefined;
  const previewFrequencyStep = (() => {
    if (previewFrequencyUnit === 'hour') return 0.1 / 24;
    if (previewFrequencyUnit === 'day') return 0.1;
    if (previewFrequencyUnit === 'week') return 0.1 / 7;
    if (previewFrequencyUnit === 'month') return 0.1 / 30;
    return 0.1;
  })();

  const avgDurationThresholds =
    avgDurationWarnSeconds !== null && avgDurationTargetSeconds !== null
      ? {
          warn: avgDurationWarnSeconds,
          target: avgDurationTargetSeconds,
          direction: 'lowerBetter' as const,
        }
      : null;

  const passRatePreviewValues = buildPreviewValues(
    { warn: passRateRange[0], target: passRateRange[1], direction: 'higherBetter' },
    { min: 0, max: 100, step: 1 },
  );

  const frequencyPreviewValues =
    frequencyThresholds !== null
      ? buildPreviewValues(frequencyThresholds, {
          min: 0,
          max: Math.max(
            frequencyThresholds.target + previewFrequencyStep,
            frequencyThresholds.warn + previewFrequencyStep,
          ),
          step: previewFrequencyStep,
        })
      : null;

  const avgDurationPreviewValues =
    avgDurationThresholds !== null
      ? buildPreviewValues(avgDurationThresholds, {
          min: 0,
          max: Math.max(avgDurationThresholds.warn + 1, avgDurationThresholds.target + 1),
          step: 1,
        })
      : null;

  const actions = (
    <Inline justify="flex-end" gap="sm">
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disabled={!hasPendingChanges}
        onClick={() => {
          setHasInteracted(false);
          setEnabled(resolvedInitialEnabled);
          onCancel();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        fullWidthOnMobile={false}
        disabled={!canSubmit}
        onClick={() => {
          onSubmit(enabled);
        }}
      >
        Update
      </Button>
    </Inline>
  );

  const sections = (
    <Stack gap="xl">
      <Box sx={{ py: s('xs') }}>
        <Divider />
      </Box>
      <MetricSubsection
        title={passRateLabel}
        headerAccessory={
          <Switch
            size="small"
            checked={enabled.passRate}
            onChange={(_event, checked) => {
              setHasInteracted(true);
              setEnabled((prev) => ({ ...prev, passRate: checked }));
            }}
          />
        }
      >
        <Stack>
          <Stack gap="none">
            <Slider
              value={passRateRange}
              min={0}
              max={100}
              step={1}
              disabled={!enabled.passRate}
              valueLabelDisplay="auto"
              disableSwap
              sx={{
                height: s('md'),
                pb: s('sm'),
                '& .MuiSlider-rail': {
                  backgroundImage: passRateRailBackground,
                  opacity: 1,
                  height: s('md'),
                  border: 'none',
                },
                '& .MuiSlider-track': {
                  backgroundColor: 'transparent',
                  height: s('md'),
                  border: 'none',
                },
                '& .MuiSlider-thumb': {
                  width: s('xxs'),
                  height: s('xl'),
                  backgroundColor: 'common.white',
                  border: '1.5px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  borderRadius: s('xs'),
                  '&:hover, &.Mui-focusVisible, &.Mui-active': {
                    boxShadow: 'none',
                  },
                },
              }}
              onChange={(_event, value) => {
                if (!Array.isArray(value) || value.length < 2) return;
                const [nextWarn, nextTarget] = value;
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  passRate: {
                    ...prev.passRate,
                    warn: String(nextWarn),
                    target: String(nextTarget),
                  },
                }));
              }}
            />
            <Inline justify="space-between">
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Warn below {passRateRange[1]}%
              </Text>
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Alert below {passRateRange[0]}%
              </Text>
            </Inline>
          </Stack>
          <ThresholdPreviewChips
            values={passRatePreviewValues}
            formatter={(value) => `${Math.round(value)}%`}
            direction="higherBetter"
            warn={passRateRange[0]}
            target={passRateRange[1]}
            disabled={!enabled.passRate}
          />
        </Stack>
      </MetricSubsection>

      <Box sx={{ py: s('xs') }}>
        <Divider />
      </Box>

      <MetricSubsection
        title={frequencyLabel}
        headerAccessory={
          <Switch
            size="small"
            checked={enabled.frequency}
            onChange={(_event, checked) => {
              setHasInteracted(true);
              setEnabled((prev) => ({ ...prev, frequency: checked }));
            }}
          />
        }
      >
        <Stack gap="lg">
          <Stack gap="lg">
            <ThresholdValueUnitRow
              label="Warn below"
              value={frequencyDraft.targetValue}
              unit={frequencyDraft.targetUnit}
              templateColumns={frequencyThresholdRowTemplateColumns}
              options={frequencyUnitOptions}
              betweenText={executionSubjectLabel(frequencyDraft.targetValue)}
              disabled={!enabled.frequency}
              useNumberField
              error={
                parseFrequencyPerDay(frequencyDraft.targetValue, frequencyDraft.targetUnit) === null
              }
              onValueChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  frequency: { ...prev.frequency, targetValue: nextValue },
                }));
              }}
              onUnitChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  frequency: { ...prev.frequency, targetUnit: nextValue },
                }));
                preferredUnitsRef.current = {
                  ...preferredUnitsRef.current,
                  frequencyTarget: nextValue,
                };
              }}
            />
            <ThresholdValueUnitRow
              label="Alert below"
              value={frequencyDraft.warnValue}
              unit={frequencyDraft.warnUnit}
              templateColumns={frequencyThresholdRowTemplateColumns}
              options={frequencyUnitOptions}
              betweenText={executionSubjectLabel(frequencyDraft.warnValue)}
              disabled={!enabled.frequency}
              useNumberField
              error={
                parseFrequencyPerDay(frequencyDraft.warnValue, frequencyDraft.warnUnit) === null
              }
              onValueChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  frequency: { ...prev.frequency, warnValue: nextValue },
                }));
              }}
              onUnitChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  frequency: { ...prev.frequency, warnUnit: nextValue },
                }));
                preferredUnitsRef.current = {
                  ...preferredUnitsRef.current,
                  frequencyWarn: nextValue,
                };
              }}
            />
          </Stack>
          {frequencyPreviewValues && frequencyThresholds ? (
            <ThresholdPreviewChips
              values={frequencyPreviewValues}
              formatter={(value) => formatFrequency(value, previewFrequencyUnit)}
              direction="higherBetter"
              warn={frequencyThresholds.warn}
              target={frequencyThresholds.target}
              disabled={!enabled.frequency}
            />
          ) : null}
        </Stack>
      </MetricSubsection>

      <Box sx={{ py: s('xs') }}>
        <Divider />
      </Box>

      <MetricSubsection
        title="Average Duration"
        headerAccessory={
          <Switch
            size="small"
            checked={enabled.avgDuration}
            onChange={(_event, checked) => {
              setHasInteracted(true);
              setEnabled((prev) => ({ ...prev, avgDuration: checked }));
            }}
          />
        }
      >
        <Stack gap="lg">
          <Stack gap="lg">
            <ThresholdValueUnitRow
              label="Warn above"
              value={avgDurationDraft.targetValue}
              unit={avgDurationDraft.targetUnit}
              templateColumns={avgDurationThresholdRowTemplateColumns}
              options={avgDurationUnitOptions}
              betweenText={avgDurationPerLabel}
              contextAtEnd
              pluralizeUnitLabelsByValue
              disabled={!enabled.avgDuration}
              useNumberField
              error={
                parseAvgDurationSeconds(
                  avgDurationDraft.targetValue,
                  avgDurationDraft.targetUnit,
                ) === null
              }
              onValueChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  avgDuration: { ...prev.avgDuration, targetValue: nextValue },
                }));
              }}
              onUnitChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  avgDuration: { ...prev.avgDuration, targetUnit: nextValue },
                }));
                preferredUnitsRef.current = {
                  ...preferredUnitsRef.current,
                  avgDurationTarget: nextValue,
                };
              }}
            />
            <ThresholdValueUnitRow
              label="Alert above"
              value={avgDurationDraft.warnValue}
              unit={avgDurationDraft.warnUnit}
              templateColumns={avgDurationThresholdRowTemplateColumns}
              options={avgDurationUnitOptions}
              betweenText={avgDurationPerLabel}
              contextAtEnd
              pluralizeUnitLabelsByValue
              disabled={!enabled.avgDuration}
              useNumberField
              error={
                parseAvgDurationSeconds(avgDurationDraft.warnValue, avgDurationDraft.warnUnit) ===
                null
              }
              onValueChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  avgDuration: { ...prev.avgDuration, warnValue: nextValue },
                }));
              }}
              onUnitChange={(nextValue) => {
                setHasInteracted(true);
                setDrafts((prev) => ({
                  ...prev,
                  avgDuration: { ...prev.avgDuration, warnUnit: nextValue },
                }));
                preferredUnitsRef.current = {
                  ...preferredUnitsRef.current,
                  avgDurationWarn: nextValue,
                };
              }}
            />
          </Stack>
          {avgDurationPreviewValues && avgDurationThresholds ? (
            <ThresholdPreviewChips
              values={avgDurationPreviewValues}
              formatter={(value) => formatDuration(value * 1000)}
              direction="lowerBetter"
              warn={avgDurationThresholds.warn}
              target={avgDurationThresholds.target}
              disabled={!enabled.avgDuration}
            />
          ) : null}
        </Stack>
      </MetricSubsection>
    </Stack>
  );

  if (container === 'none') {
    return (
      <Stack gap="md">
        {sections}
        <Box sx={{ paddingTop: s('md') }}>{actions}</Box>
      </Stack>
    );
  }

  return (
    <Panel
      title={title}
      description={
        <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
          {description}
        </Text>
      }
      actions={actions}
    >
      {sections}
    </Panel>
  );
};
